import { UnicodeEntity } from '@mews/enums';
import { InputValue, Size, Status, useComponentProps } from '@optimus-web/common';
import { Asterisk } from '../common/FormLabel';
import { FormMessage } from '../common/FormMessage';
import {
    CheckboxContainer,
    CheckboxElement,
    CheckMarkBackgroundElement,
    CheckMarkPathElement,
    CheckMarkSvgElement,
    InputWrapper,
    LabelElement,
} from './style';

export interface CheckboxProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        'onChange' | 'value' | 'name' | 'size' | 'onFocus' | 'onBlur'
    > {
    error?: React.ReactNode;
    'data-test-id'?: string;
    indeterminate?: boolean;
    name?: string;
    onChange?: (event: React.SyntheticEvent<HTMLElement>, checked: boolean) => void;
    onFocus?: React.FocusEventHandler<HTMLButtonElement>;
    onBlur?: React.FocusEventHandler<HTMLButtonElement>;
    size?: Exclude<Size, 'extraLarge'>;
    value?: InputValue;
    stopClickPropagation?: boolean;
    as?: React.ElementType;
}

export const Checkbox = ({
    children,
    disabled,
    id,
    name,
    value,
    tabIndex,
    checked,
    required,
    className,
    error,
    size: customSize,
    indeterminate,
    onChange,
    onFocus,
    onBlur,
    'data-test-id': dataTestId,
    stopClickPropagation,
    as,
    ...other
}: CheckboxProps) => {
    const { size } = useComponentProps('Checkbox', { size: customSize });

    const handleChange = (event: React.SyntheticEvent<HTMLElement>) => {
        onChange?.(event, !checked);
    };

    const handleCheckboxClick = (event: React.SyntheticEvent<HTMLElement>) => {
        event.preventDefault();
        if (stopClickPropagation) {
            event.stopPropagation();
        }
        (event.target as HTMLInputElement).checked = !checked;
        handleChange(event);
    };

    const finalId = id || (name || value ? `${name}-${value}` : undefined);

    return (
        <CheckboxContainer as={as} className={className} data-test-id={dataTestId}>
            <InputWrapper htmlFor={finalId} disabled={disabled} withoutLabel={!children}>
                <CheckboxElement
                    type="checkbox"
                    name={name}
                    value={value}
                    onChange={handleChange}
                    checked={checked}
                    id={finalId}
                    disabled={disabled}
                    tabIndex={-1}
                    aria-checked={indeterminate ? 'mixed' : checked}
                    required={required}
                    aria-required={required}
                    aria-errormessage={error ? `${name}-error` : undefined}
                    aria-invalid={!!error}
                    {...other}
                />
                <CheckMarkBackgroundElement
                    checked={checked}
                    disabled={disabled}
                    size={size}
                    indeterminate={indeterminate}
                    withoutLabel={!children}
                    error={!!error}
                    onClick={handleCheckboxClick}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    type="button"
                    aria-label={finalId}
                    aria-labelledby={finalId}
                    role="checkbox"
                    aria-checked={indeterminate ? 'mixed' : checked}
                >
                    <CheckMarkSvgElement
                        checked={checked}
                        indeterminate={indeterminate}
                        width={size === 'large' ? '15' : '10'}
                        height={size === 'large' ? '12' : '8'}
                        viewBox="0 0 10 8"
                        fill="none"
                    >
                        <CheckMarkPathElement
                            checked={checked}
                            disabled={disabled}
                            d="M8.23196 0.361083C8.58565 -0.0630916 9.21623 -0.120233 9.64041 0.233454C10.032 0.559934 10.1108 1.12235 9.84238 1.54057L9.76804 1.6419L4.7685 7.63781C4.41883 8.05716 3.80491 8.11368 3.38616 7.78665L3.2932 7.70436L0.292741 4.70261C-0.0976991 4.312 -0.097563 3.67884 0.293045 3.2884C0.653607 2.92799 1.22084 2.90038 1.61307 3.20549L1.70726 3.2887L3.933 5.51508L8.23196 0.361083Z"
                        />
                    </CheckMarkSvgElement>
                </CheckMarkBackgroundElement>
                {children ? (
                    <LabelElement size={size} required={required}>
                        {children}
                        {required ? <Asterisk aria-hidden>{UnicodeEntity.Asterisk}</Asterisk> : null}
                    </LabelElement>
                ) : null}
            </InputWrapper>
            {error ? (
                <FormMessage status={Status.Error} id={`${name}-error`}>
                    {error}
                </FormMessage>
            ) : null}
        </CheckboxContainer>
    );
};
